import {
  Component,
  ElementRef,
  inject,
  OnInit,
  output,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Router } from '@angular/router';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import {
  ProgressComponent,
  IconComponent,
  ObserveIntersectingDirective,
  MessageService
} from '@factor_ec/ui';
import { Operation, StorageService } from '@factor_ec/utils';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';

import { AppService } from '@/core/app.service';
import { Response } from '@/main/models/response';
import { Catalog } from '@/core/models/catalog';
import { Conditions, PaymentTable, Simulation } from '@/main/models/simulation';
import { SessionService } from '@/core/session.service';
import { ValidationRule } from '@/main/models/validation-rule';
import { ApiService } from '@/core/api.service';
import { SimulationService } from '@/main/services/simulation.service';
import { environment } from '@/environments/environment';
import { MarketRates } from '@/main/models/market-rates';
import { twoDecimalPlacesValidator } from '@/main/utils/twoDecimalPlacesValidator';
import { integerValidator } from '@/main/utils/integerValidator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AdmobService } from '@/core/admod.service';
import { BeneficiaryRepository } from '@/main/repositories/beneficiary-repository';
import { GroupRepository } from '@/main/repositories/group-repository';
import { Beneficiary } from '@/main/models/connection';
import { Group } from '@/main/models/group';
import { ConnectionService } from '@/main/services/connection.service';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';
import { GroupService } from '@/main/services/group.service';

/**
 * @description Componente para realizar simulaciones de crédito
 * @class SimulationComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-simulation',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    IconComponent,
    ObserveIntersectingDirective,
    ProgressComponent,
    MatToolbarModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatMenuModule,
    MatSelectModule,
    MatStepperModule,
    MatTooltipModule,
    IconComponent,
    ProgressComponent,
    IsGrantedDirective
  ],
  templateUrl: './simulation.component.html',
  styleUrl: './simulation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimulationComponent implements OnInit {
  // Dependency injection
  private readonly admobService = inject(AdmobService);
  private readonly apiService = inject(ApiService);
  public readonly appService = inject(AppService);
  public readonly beneficiaryRepository = inject(BeneficiaryRepository);
  private readonly connectionService = inject(ConnectionService);
  public readonly groupRepository = inject(GroupRepository);
  private readonly groupService = inject(GroupService);
  private readonly sessionService = inject(SessionService);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly router: Router = inject(Router);
  private readonly simulationService = inject(SimulationService);
  private readonly storageService = inject(StorageService);
  public readonly data: {
    simulation?: Simulation;
    group?: Group;
    type?: 'simulation' | 'credit';
  } = inject(MAT_DIALOG_DATA);

  // Events
  public readonly afterSubmit = output<Operation>();

  // Properties
  public allowSharing = signal<boolean>(false);
  public amountSample!: string;
  public beneficiaries = this.beneficiaryRepository.findBy();
  public groups = this.groupRepository.findBy();
  public readonly requiredAmountMessage = $localize`Enter the amount`;
  public readonly requiredTermMessage = $localize`Enter the term`;
  public form: FormGroup = new FormGroup({});
  public messageCommaNotAllowed = signal<string>($localize`Comma not allowed`);
  public simulation = signal<Simulation | undefined>(undefined);
  public submitting = signal<boolean>(false);
  public readonly today = new Date();
  public conditions = signal<Conditions | undefined>(undefined);
  public simulationDocument = signal<string | undefined>(undefined);
  public marketSimulation = signal<any>(undefined);
  public marketRates = signal<MarketRates | undefined>(undefined);
  public mode = signal<'add' | 'update'>('add');
  public simulationForm: FormGroup = new FormGroup({});
  public propertiesForm: FormGroup = new FormGroup({});
  public loading = signal<boolean>(false);
  public sharing = signal<boolean>(false);
  public currencies = signal<Catalog[]>([]);
  public countries = signal<Catalog[]>([]);
  public frequencies = signal<Catalog[]>([]);
  public tableTypes = signal<Catalog[]>([]);
  public type = signal<'simulation' | 'credit'>('simulation');
  public readonly totalToReceive = $localize`Total to receive`;
  public validationRules = signal<ValidationRule[]>([]);
  public pageLoading = signal<boolean>(false);
  public readonly pageLimit = environment.pageLimit;
  public pageNumber = 1;
  public pageCompleted = signal<boolean>(false);
  public effectiveDateMin = signal<Date | undefined>(undefined);
  public effectiveDateMax = signal<Date | undefined>(undefined);
  public firstPaymentDateMax = signal<Date | undefined>(undefined);
  public profileId = this.sessionService.params()['profileId'];
  public profileIds  = PROFILE_IDS;

  @ViewChild('contentElement') contentElement!: ElementRef;
  @ViewChild('stepper') stepper!: MatStepper;

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    if (this.data?.type) {
      this.type.set(this.data?.type);
    }
    this.loading.set(false);
    this.allowSharing.set(
      // Capacitor.isNativePlatform() || typeof navigator.share === 'function'
      Capacitor.isNativePlatform()
    );
    this.initForm();
    if (this.data?.simulation?.operationNumber) {
      this.mode.set('update');
      this.mapForm(this.data?.simulation);
    }
    if (Capacitor.getPlatform() !== 'web') {
      this.admobService.init();
    }
  }

  /**
   * @description Regresa a la página anterior
   * @returns {void}
   */
  back(): void {
    if (this.stepper.selectedIndex > 0 && this.type() === 'simulation') {
      this.stepper.selectedIndex--;
    } else if (this.stepper.selectedIndex === 2 && this.type() === 'credit') {
      this.stepper.selectedIndex = 0;
    } else {
      this.dialogRef.close();
    }
    this.contentElement?.nativeElement.scrollTo(0, 0);
  }

  /**
   * @description Compara dos ítems por sequence para uso en mat-select
   * @param {any} a - Primer ítem a comparar
   * @param {any} b - Segundo ítem a comparar
   * @returns {boolean} true si son iguales por sequence, false en caso contrario
   */
  compareItem(a: any, b: any): boolean {
    return a && b ? a.sequence === b.sequence : a === b;
  }

  /**
   * @description Obtiene los catálogos
   * @returns {Promise<void>}
   */
  getCatalogs(): void {
    this.countries.set(
      this.sessionService.catalogs().filter(c => c.catalogId === 'CPAMD')
    );
    this.currencies.set(
      this.sessionService.catalogs().filter(c => c.catalogId === 'MONED')
    );
    this.frequencies.set(
      this.sessionService.catalogs().filter(c => c.catalogId === 'GPERI')
    );
    this.tableTypes.set(
      this.sessionService.catalogs().filter(c => c.catalogId === 'GSAMZ')
    );
  }

  /**
   * @description Obtiene la tabla de amortización
   * @param {string} operationNumber - Número de operación
   * @param {number} pageNumber - Número de página
   * @returns {Promise<PaymentTable[]>} Tabla de amortización
   */
  async getPaymentTable(
    operationNumber: string,
    pageNumber: number
  ): Promise<PaymentTable[]> {
    this.pageLoading.set(true);
    const response: any = await this.apiService.call({
      microServiceName: 'WSMPCreditManagement',
      resourcePath: `/offercredit/get-amort-table/{operationNumber}/${this.pageLimit}/${pageNumber}`,
      method: 'GET',
      useToken: true,
      pathParams: {
        operationNumber: { value: operationNumber, encrypted: true }
      },
      responseEncrypted: true
    });
    this.pageLoading.set(false);
    this.pageCompleted.set(response.data.length < this.pageLimit);
    this.pageNumber = pageNumber;
    // @todo la asignación del valor de la cuota es temporal mientras se actualiza en el servicio
    return response.data;
  }

  /**
   * @description Obtiene las condiciones de la simulación
   * @param {number} pageNumber - Número de página
   * @returns {Promise<Conditions | undefined>} Condiciones de la simulación
   */
  async getConditions(pageNumber: number): Promise<Conditions | null> {
    try {
      const simulationRequest = this.simulationService.mapRequest(
        this.simulationForm.getRawValue()
      );
      simulationRequest.limit = this.pageLimit;
      simulationRequest.pageNumber = pageNumber;
      this.pageLoading.set(true);
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPSimulations',
        resourcePath: `/core/get-gaf-simulations`,
        method: 'POST',
        body: simulationRequest,
        bodyEncrypted: true
      });
      if (!this.simulation()) {
        this.simulation.set(response.data);
      } else {
        this.simulation.update(simulation => {
          return { ...simulation, ...response.data };
        });
      }
      this.appService.restrictions.update(r => {
        r.numOffersToRegister = response.data.numOffersToRegister;
        return r;
      });
      this.pageCompleted.set(
        response.data.paymentTable.length < this.pageLimit
      );
      this.pageNumber = pageNumber;
      return response.data;
    } catch (error) {
      this.pageCompleted.set(true);
      return null;
    } finally {
      this.pageLoading.set(false);
    }
  }

  /**
   * @description Obtiene la fecha mínima
   * @returns {Date | null} Fecha mínima
   */
  getMinDate(): Date | null {
    const effectiveDate = this.simulationForm.get('effectiveDate')?.value;
    if (effectiveDate) {
      const minDate = new Date(effectiveDate);
      minDate.setDate(minDate.getDate() + 1); // Añade 1 día
      return minDate;
    }
    return null;
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private async initForm(): Promise<void> {
    const subscriptionLevelId = this.sessionService.params()['profileId'];
    this.getCatalogs();
    let notifications = {};
    if (this.type() === 'credit') {
      notifications = {
        notifyOverduePayment: false,
        notifyPayment: false,
        notifySendingReceipts: false
      };
    }
    this.simulationForm = this.formBuilder.group({
      amount: [
        '',
        [
          Validators.required,
          twoDecimalPlacesValidator(),
          Validators.pattern(/^[0-9]+(\.[0-9]*)?$/)
        ]
      ],
      country: ['', Validators.required],
      term: ['', [Validators.required, integerValidator()]],
      frequency: ['', Validators.required],
      tableType: ['', Validators.required],
      rate: [
        '',
        [
          Validators.required,
          twoDecimalPlacesValidator(),
          Validators.pattern(/^[0-9]+(\.[0-9]*)?$/)
        ]
      ],
      effectiveDate: ['', Validators.required],
      firstPaymentDate: [{ value: '', disabled: true }, Validators.required]
    });
    this.simulationForm.get('country')?.valueChanges.subscribe(async value => {
      // Actualiza reglas de solicitud
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPApplicationSettings',
        resourcePath: `/appsrv/get-validation-rules/${value.value}/CON/${subscriptionLevelId}`,
        method: 'GET'
      });

      this.validationRules.set(
        response.data.find((d: any) => d.countryId === value.id).validations
      );
      this.setValidations(this.validationRules());

      // Obtiene las tasas del mercado
      const showMarketRates = this.validationRules().some(
        r => r.id === 'MPSTM' && r.value === 'S'
      );
      if (showMarketRates && this.type() === 'simulation') {
        const marketRates = (await this.apiService.call({
          microServiceName: 'WSMPSimulations',
          resourcePath: `/market_rates/get-market-rates/${value.id}`,
          method: 'GET'
        })) as Response;
        this.marketRates.set(marketRates.data as MarketRates);
      } else {
        this.marketRates.set(undefined);
      }
      // Como las validaciones de plazo dependen del país y la frecuencia, se actualiza el valor de frecuencia para sincronizarlas
      this.simulationForm.get('frequency')?.updateValueAndValidity();
    });
    this.simulationForm.get('frequency')?.valueChanges.subscribe(value => {
      if (this.simulationForm.enabled && value) {
        const minTerm = this.validationRules().find(r => r.id === 'MPPCN');
        const maxTerm = this.validationRules().find(r => r.id === 'MPPCX');
        const termControl = this.simulationForm.get('term');
        let max = 1;
        switch (value.id) {
          case 'C07':
            max = 52;
            break;
          case 'C15':
            max = 24;
            break;
          case 'C30':
            max = 12;
            break;
          case 'C60':
            max = 6;
            break;
          case 'C90':
            max = 4;
            break;
        }
        termControl?.setValidators([
          Validators.required,
          integerValidator(),
          Validators.min(Number(minTerm?.value)),
          Validators.max(Number(maxTerm?.value) * max)
        ]);
        termControl?.updateValueAndValidity();
        this.setValidations();
      }
    });
    this.simulationForm.get('effectiveDate')?.valueChanges.subscribe(value => {
      if (this.simulationForm.enabled && value) {
        this.simulationForm.get('firstPaymentDate')?.enable();
        this.setValidations();
      }
    });
    this.propertiesForm = this.formBuilder.group({
      beneficiary: [null as Beneficiary | null, Validators.required],
      group: [this.data.group || { sequence: 999 } as Group],
      ...notifications
    });

    // Asignar el valor en el formulario
    this.simulationForm
      .get('frequency')
      ?.setValue(
        this.frequencies().find(p => p.id === 'C30') || this.frequencies()[0],
        { emitEvent: false }
      );
    this.simulationForm
      .get('tableType')
      ?.setValue(
        this.tableTypes().find(t => t.id === 'FRA') || this.tableTypes()[0]
      );
    const country: Catalog =
      this.countries()?.find(c =>
        this.data?.simulation?.operationNumber
          ? c.id === this.data?.simulation?.countryId
          : c.value === this.sessionService.settings()?.residenceCurrency.id &&
            c.id === this.sessionService.settings()?.countryResidence.id
      ) || this.countries()[0];
    this.simulationForm.get('country')?.setValue(country);
  }

  /**
   * @description Pasa a la siguiente página
   * @param {any} event - Evento
   * @returns {Promise<void>}
   */
  async nextPage(event: any): Promise<void> {
    if (
      event &&
      !this.pageCompleted() &&
      !this.pageLoading() &&
      (this.simulation()?.paymentTable?.length || 0) > 0 &&
      this.type() === 'simulation'
    ) {
      const paymentTable =
        structuredClone(this.conditions()?.paymentTable) || [];
      const conditions = await this.getConditions(this.pageNumber + 1);
      if (conditions) {
        conditions.paymentTable = paymentTable.concat(
          conditions?.paymentTable || []
        );
        this.conditions.set(conditions);
      }
    }
  }

  /**
   * @description Mapea los datos de la simulación
   * @param {Simulation} data - Datos de la simulación
   * @returns {Promise<void>}
   */
  async mapForm(data: Simulation): Promise<void> {
    const simulation: any = structuredClone(data);
    simulation.tableType = this.tableTypes().find(
      t => t.id === simulation.tableType.id
    ) as Catalog;
    simulation.frequency = this.frequencies().find(
      f => f.id === simulation.frequency.id
    ) as Catalog;
    simulation.effectiveDate = new Date(simulation.effectiveDate + 'T00:00:00');
    simulation.firstPaymentDate = new Date(
      simulation.firstPaymentDate + 'T00:00:00'
    );
    this.simulationForm.patchValue(simulation);
    await this.loadProperties();
    const beneficiary = (this.beneficiaries.accumulated() ?? []).find(
      b => b.sequence === simulation.beneficiary?.sequence
    );
    const group = (this.groups.accumulated() ?? []).find(
      g => g.sequence === (simulation as any).group.sequence
    );
    this.propertiesForm.patchValue({
      beneficiary: beneficiary ?? null,
      group: group ?? null,
      notifyPayment: simulation.beneficiary?.notifyPayment,
      notifyOverduePayment: simulation.beneficiary?.notifyOverduePayment,
      notifySendingReceipts: simulation.beneficiary?.notifySendingReceipts
    });
    this.mode.set('update');
    simulation.paymentTable = await this.getPaymentTable(
      simulation.operationNumber,
      1
    );
    this.simulation.set(simulation);
  }

  /**
   * @description Mapea los datos de la solicitud
   * @param {any} data - Datos a mapear
   * @returns {any} Datos mapeados
   */
  mapRequest(data: any): any {
    let request = structuredClone(data);
    const lenderId = this.sessionService.settings()?.lenderId;
    request.lenderId = lenderId;
    request.countryId = this.sessionService.settings()?.countryResidence.id;
    request.productTypeId = 'CON';
    return request;
  }

  /**
   * @description Abre el diálogo de beneficiario y actualiza el formulario con el resultado
   * @returns {Promise<void>}
   */
  async openBeneficiary(): Promise<void> {
    const operation = await this.connectionService.openBeneficiary();
    if (operation) {
      this.beneficiaries.reload();
      this.propertiesForm
        .get('beneficiary')
        ?.setValue(operation.entity as Beneficiary);
    }
  }

  /**
   * @description Abre el diálogo de grupo y actualiza el formulario con el resultado
   * @returns {Promise<void>}
   */
  async openGroup(): Promise<void> {
    const operation = await this.groupService.open();
    if (operation) {
      this.groups.reload();
      this.propertiesForm.get('group')?.setValue(operation.entity as Group);
    }
  }

  /**
   * @description Desplaza el contenido hacia arriba
   * @param {StepperSelectionEvent} event - Evento
   * @returns {void}
   */
  public scrollToTop(event: StepperSelectionEvent): void {
    const container = document.getElementById('scrollable-container');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (event.selectedIndex === 2) {
      this.loadProperties();
    }
  }

  /**
   * @description Carga beneficiarios y grupos para el formulario de propiedades
   * @private
   * @returns {Promise<void>}
   */
  private async loadProperties(): Promise<void> {
    const lenderId = this.sessionService.settings()?.lenderId;
    if (lenderId) {
      await Promise.all([
        this.beneficiaries.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        }),
        this.groups.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        })
      ]);
    }
  }

  /**
   * @description Establece el país
   * @param {Catalog} country - País
   * @returns {Promise<void>}
   */
  async setCountry(country: Catalog): Promise<void> {
    this.simulationForm.get('country')?.setValue(country);
  }

  /**
   * @description Establece la frecuencia
   * @param {Catalog} frequency - Frecuencia
   * @returns {void}
   */
  setFrequency(frequency: Catalog): void {
    this.simulationForm.get('frequency')?.setValue(frequency);
  }

  /**
   * @description Mapea los datos del formulario de propiedades al payload de la API
   * @private
   * @returns {{ beneficiary: { sequence: number; alias: string }; group?: { sequence: number } }} Payload formateado
   */
  private mapPropertiesPayload(): {
    beneficiary: { sequence: number; alias: string };
    group?: { sequence: number | null };
  } {
    const formValue = this.propertiesForm.getRawValue();
    const beneficiary = formValue.beneficiary as Beneficiary | null;
    const group = formValue.group as Group | null;
    const payload: {
      beneficiary: {
        sequence: number;
        alias: string;
        notifyOverduePayment: boolean;
        notifyPayment: boolean;
        notifySendingReceipts: boolean;
      };
      group?: { sequence: number | null };
    } = {
      beneficiary: {
        sequence: beneficiary!.sequence,
        alias: beneficiary!.description,
        notifyOverduePayment: formValue.notifyOverduePayment,
        notifyPayment: formValue.notifyPayment,
        notifySendingReceipts: formValue.notifySendingReceipts
      }
    };
    if (group?.sequence != null) {
      payload.group = { sequence: group.sequence !== 999 ? group.sequence : null };
    }
    return payload;
  }

  /**
   * @description Establece las reglas de validación
   * @param {ValidationRule[]} rules - Reglas de validación
   * @returns {void}
   */
  setValidations(rules?: ValidationRule[]): void {
    rules = !rules ? this.validationRules() : rules;
    const amountControl = this.simulationForm.get('amount');
    amountControl?.setValidators([
      Validators.required,
      twoDecimalPlacesValidator()
    ]);
    const rateControl = this.simulationForm.get('rate');
    rateControl?.setValidators([
      Validators.required,
      twoDecimalPlacesValidator()
    ]);
    const frequencyDays = parseInt(
      this.simulationForm.get('frequency')?.value.value.replace(/\D/g, ''),
      10
    );
    rules.forEach((rule: ValidationRule) => {
      switch (rule.id) {
        // Fecha efectiva: meses perm. hacia atrás
        case 'MPFEA':
          const minEffectiveDate = moment();
          minEffectiveDate.subtract(Number(rule.value), 'months');
          this.effectiveDateMin.set(minEffectiveDate.toDate());
          break;
        // Fecha efectiva: meses perm. hacia adelante
        case 'MPFES':
          const maxEffectiveDate = moment();
          if (this.type() === 'simulation') {
            maxEffectiveDate.add(Number(rule.value), 'months');
          } else {
            maxEffectiveDate.subtract(1, 'day');
          }
          this.effectiveDateMax.set(maxEffectiveDate.toDate());
          break;
        // Fec.1er pag: períodos perm. hacia adelante
        case 'MPFPS':
          if (this.simulationForm.get('effectiveDate')?.value) {
            let maxFirstPaymentDate = moment(
              this.simulationForm.get('effectiveDate')?.value
            );
            const maxDays = frequencyDays * Number(rule.value);
            maxFirstPaymentDate.add(maxDays, 'days');
            this.firstPaymentDateMax.set(maxFirstPaymentDate.toDate());
          }
          break;
        // Monto financiado: mínimo
        case 'MPMFN':
          this.amountSample =
            new Intl.NumberFormat(navigator.language, {
              useGrouping: false,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(Number(rule.value)) +
            ' ' +
            this.simulationForm.get('country')?.value.value;
          amountControl?.addValidators([Validators.min(Number(rule.value))]);
          amountControl?.updateValueAndValidity();
          break;
        // Monto financiado: máximo
        case 'MPMFX':
          amountControl?.addValidators([Validators.max(Number(rule.value))]);
          amountControl?.updateValueAndValidity();
          break;
        // Tasa interés nominal: mínimo
        case 'MPTNN':
          rateControl?.addValidators([Validators.min(Number(rule.value))]);
          rateControl?.updateValueAndValidity();
          break;
        // Tasa interés nominal: máximo
        case 'MPTNX':
          rateControl?.addValidators([Validators.max(Number(rule.value))]);
          rateControl?.updateValueAndValidity();
          break;
        // Monto máximo de créditos
        case 'MPMMC':
          this.appService.restrictions.update(r => {
            r.maxCreditAmount = Number(rule.value);
            return r;
          });
          break;
      }
    });
  }

  /**
   * @description Comparte la simulación
   * @param {Simulation} simulation - Simulación
   * @returns {Promise<void>}
   */
  async share(simulation: Simulation, download?: boolean): Promise<void> {
    this.sharing.set(true);
    await this.simulationService.shareSimulation(simulation, download);
    this.sharing.set(false);
  }

  /**
   * @description Envía el formulario para procesar la simulación
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.simulationForm.valid) {
      // Condiciones
      if (this.stepper.selectedIndex === 0) {
        this.simulationForm.disable();
        this.submitting.set(true);
        this.conditions.set((await this.getConditions(1)) as Conditions);
      }
      if (this.type() === 'simulation' && this.stepper.selectedIndex === 0) {
        try {
          const showMarketRates = this.validationRules().some(
            r => r.id === 'MPSTM' && r.value === 'S'
          );
          if (!this.allowBySubscription('register')) {
            return;
          }
          if (showMarketRates) {
            const marketRequest = {
              amount: this.simulationForm.get('amount')?.value,
              term: this.simulationForm.get('term')?.value,
              ...this.marketRates()
            };
            const marketSimulation: any = await this.apiService.call({
              microServiceName: 'WSMPSimulations',
              resourcePath: `/market_rates/get-math-simulations`,
              method: 'POST',
              body: marketRequest
            });
            this.marketSimulation.set(marketSimulation.data as Simulation);
          }
        } finally {
          this.simulationForm.enable();
          this.submitting.set(false);
        }
      } else if (this.type() === 'credit' && this.stepper.selectedIndex === 0) {
        this.simulationForm.enable();
        this.submitting.set(false);
        if (!this.allowBySubscription('activate')) {
          return;
        }
        await this.loadProperties();
        this.stepper.selectedIndex = 2;
        this.contentElement?.nativeElement.scrollTo(0, 0);
        return;
        // Vista de registro de la oferta
      } else if (this.stepper.selectedIndex === 2) {
        try {
          this.propertiesForm.disable();
          this.submitting.set(true);
          const lenderId = this.sessionService.settings()?.lenderId;
          const operationProperties =
            this.mode() === 'update'
              ? { operationNumber: this.simulation()?.operationNumber }
              : { lenderId };
          let response;
          if (this.type() === 'simulation') {
            response = await this.apiService.call({
              microServiceName: 'WSMPCreditManagement',
              resourcePath:
                this.mode() === 'update'
                  ? '/offer/edit-offer'
                  : '/offer/register-offer',
              method: this.mode() === 'update' ? 'PUT' : 'POST',
              useToken: true,
              body: {
                ...operationProperties,
                simulation: this.simulationService.mapRequest(
                  this.simulationForm.getRawValue()
                ),
                ...this.mapPropertiesPayload()
              },
              bodyEncrypted: true
            });
          } else {
            response = await this.apiService.call({
              microServiceName: 'WSMPCreditManagement',
              resourcePath: '/credit/register-credit',
              method: 'POST',
              useToken: true,
              body: {
                ...operationProperties,
                credit: this.simulationService.mapRequest(
                  this.simulationForm.getRawValue()
                ),
                ...this.mapPropertiesPayload()
              },
              bodyEncrypted: true
            });
          }
          this.storageService.set(
            this.appService.currencyId,
            this.simulationForm.getRawValue().country.value,
            'local'
          );
          // Solo muestra publicidad en el plan gratuito
          if (
            this.sessionService
              .settings()
              ?.validations.some(v => v.id === 'MPDPC' && v.value === 'S')
          ) {
            const unitCode =
              this.type() === 'simulation'
                ? 'registerOfferUnitCode'
                : 'activateCreditUnitCode';
            if (Capacitor.getPlatform() === 'android') {
              await this.admobService.showInterstitial(
                environment.admob.android[unitCode]
              );
            } else if (Capacitor.getPlatform() === 'ios') {
              await this.admobService.showInterstitial(
                environment.admob.ios[unitCode]
              );
            }
          }
          this.router.navigateByUrl(
            this.type() === 'simulation' ? '/simulations' : '/credits'
          );
          this.dialogRef.close();
          this.messageService.show(
            response.data.operationNumber || response.data,
            {
              type: 'modal',
              class: 'text-center d-flex flex-column align-items-center gap-3',
              icon: {
                name: 'check--circle',
                class: 'text-success ft-icon--4',
                collection: 'factoricons-slim'
              },
              actions: [
                {
                  label: $localize`Accept`,
                  value: 1,
                  type: 'flat',
                  class: 'flex-grow-1'
                }
              ]
            }
          );
          this.afterSubmit.emit({
            method: this.mode(),
            entity: response.data
          });
        } finally {
          this.propertiesForm.enable();
          this.submitting.set(false);
        }
        return;
      }
      this.stepper.selectedIndex++;
      this.contentElement?.nativeElement.scrollTo(0, 0);
    }
  }

  /**
   * @description Verifica si la acción (registrar/activar) está permitida según la suscripción del usuario
   * @param {'register' | 'activate'} action - Acción a validar
   * @returns {boolean} true si está permitido, false si se muestra mensaje de restricción
   */
  allowBySubscription(action: 'register' | 'activate'): boolean {
    if (
      this.sessionService.params()['profileId'] !== this.profileIds.BasicBeneficiary &&
      this.mode() === 'add' &&
      ((action === 'register' &&
        (this.appService.restrictions()?.numOffersToRegister || 0) <= 0) ||
        (action === 'activate' &&
          (this.appService.restrictions()?.numOffersToActivate || 0) <= 0))
    ) {
      this.messageService.show(
        action === 'activate'
          ? $localize`You have reached the limit of active loans according to your current subscription.`
          : $localize`You have reached the limit of offers you can register according to your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    } else if (
      this.sessionService.params()['profileId'] !== this.profileIds.BasicBeneficiary &&
      this.mode() === 'add' &&
      action === 'activate' &&
      (this.appService.restrictions()?.amountInvested?.[
        this.simulationForm.get('country')?.value.value
      ] || 0) +
        this.simulationForm.get('amount')?.value >
        (this.appService.restrictions()?.maxCreditAmount || 0)
    ) {
      this.messageService.show(
        $localize`You are exceeding the maximum credit amount you can register under your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    }
    return true;
  }
}
